import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useRecoilState } from 'recoil';
import toast from 'react-hot-toast';
import { useMutation } from '@apollo/client';
import {
    Button, CustomFlowbiteTheme, Label, TextInput,
} from 'flowbite-react';

import WarningCard from '@/components/commons/warning_card';
import { type bioPageProps } from '@/types/globals';
import { userAtom } from '@/components/with_auth/State';
import routes from '@/services/routes';
import { BIO_NAMES, BIO_STATES, reportGeneralError } from '@/components/onboarding/Shared';
import PanelContainer from '@/components/commons/panel_container';
import { UPDATE_BRIEF, UPDATE_USER } from '@/components/onboarding/Actions';
import ChevronRight from '@/components/commons/icons/ChevronRight';
import { handleValidationErrors } from '@/services/apollo';

import BioPageHeader from './BioPageHeader';
import SectionHeader from './SectionHeader';
import SectionContainer from './SectionContainer';
// import FullFeatures from './full-features';
import CopyButton from './CopyButton';
import EmailSignature from './EmailSignature';

const customInputTheme: CustomFlowbiteTheme['textInput'] = {
    field: {
        input: {
            base: 'block w-full border disabled:cursor-not-allowed',
            sizes: {
                sm: 'py-2 px-4 sm:text-xs max-h-[37px] text-gray-500 bg-cirrusWhite font-instrument text-base font-normal leading-150',
            },
        },
    },
};

const LINKSTART = `${process.env.NEXT_PUBLIC_FRONTEND_LINK_DOMAIN}/`;

function SharePanel({
    isModal = false,
    isEdit,
    openBrief,
    extendContinue = null,
}: Partial<bioPageProps>) {
    const router = useRouter();
    const [isLoading, setIsLoading] = useState(false);
    const [formError, setFormError] = useState('');
    // const [rawBioKeyValue, setRawBioKeyValue] = useState('');
    // const [rawBioKeyError, setRawBioKeyError] = useState('');

    const [displayKey, setDisplayKey] = useState('');
    const [errors, setErrors] = useState(null);

    const [user, setUser] = useRecoilState(userAtom);
    const [updateUser] = useMutation(UPDATE_USER);
    const [updateBrief] = useMutation(UPDATE_BRIEF);

    const currentBrief = openBrief
        ? user.briefs.find((item) => item.id === openBrief.briefIndex)
        : user.briefs[0];
    const briefUrl = routes.BRIEF_URL(displayKey);

    useEffect(() => {
        setDisplayKey(currentBrief.displayKey || '');
    }, [currentBrief]);

    const handleFullFeatureClick = async () => {
        const attributes = {
            onboardingState: BIO_STATES.overview,
            bioOnly: false,
        };
        const res = await updateUser({
            variables: {
                updateUserInput: {
                    attributes,
                },
            },
        });

        const newUser = res?.data?.updateUser?.user;
        if (newUser) {
            if (formError !== '') setFormError('');
            setUser(newUser);
            router.push(routes.ONBOARDING_HOW_SIXTY_WORKS);
        } else {
            setFormError('An unexpected error occurred');
        }
    };

    const handleBriefUpdate = async (attributes) => {
        try {
            setFormError('');
            setIsLoading(true);

            const response = await updateBrief({
                variables: {
                    input: {
                        id: currentBrief.id,
                        attributes,
                    },
                },
            });

            const newErrors = response.data?.updateBrief?.errors;
            if (newErrors) {
                handleValidationErrors(newErrors, setErrors, 'brief');
            }
            const newBrief = response.data?.updateBrief?.brief;
            if (newBrief) {
                // update user
                const newUser = { ...user };
                newUser.briefs = user.briefs.map((item) => {
                    if (item.id === newBrief.id) {
                        return newBrief;
                    }
                    return item;
                });
                setUser(newUser);

                toast.success('Brief updated successfully.');
            }

            setIsLoading(false);
        } catch (err) {
            reportGeneralError(err);
        }
    };

    const composeSignature = (joiner) => {
        const copyParts = [];
        if (user.fullName) {
            copyParts.push(`<strong>${user.fullName}</strong>`);
        }
        if (user.title) {
            copyParts.push(user.title);
        }
        if (user.company) {
            copyParts.push(user.company);
        }
        if (currentBrief.displayKey) {
            copyParts.push(`My Brief: <a class='no-underline text-progressBarBlue font-base break-all' target='_blank' href='${briefUrl}'>${LINKSTART}${currentBrief.displayKey}</a>`);
        }

        const copy = copyParts.join(joiner);
        return copy;
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(briefUrl);
    };

    return (
        <>
            <PanelContainer stepNames={BIO_NAMES} paddingOverride="p-0 sm:p-8" step={5} totalSteps={5} isModal={isModal}>
                <div className="p-2 w-full">
                    <div className="w-full flex flex-wrap py-2 md:pb-10 gap-3 sm:px-5">
                        <BioPageHeader
                            header="Share your brief with your networks."
                            subHeader="To get the most from your brief, let&apos;s make it easy for others to see what you've put together."
                            subHeaderPadding=""
                        />
                        {isModal && (
                            <Button
                                color="dark"
                                className="bg-cleanSlate w-full md:w-auto flex ml-auto gap-5 mt-8 mb-6 md:mb-0 md:mt-11 md:absolute md:right-4 md:top-6"
                                onClick={() => extendContinue('', isEdit)}
                            >
                                I&apos;m done
                            </Button>
                        )}
                    </div>
                    <div className="w-full sm:ml-0">
                        {formError !== '' && (
                            <div className="w-full">
                                <WarningCard message={formError} />
                            </div>
                        )}
                        <div className="w-full flex flex-col gap-3">
                            <SectionContainer>
                                <div className="mb-[13px]">
                                    <SectionHeader
                                        number="1"
                                        headerText="Share via your personal link"
                                        subText="Select your preferred URL, then share with new contacts or people you&apos;re meeting with."
                                        customWidth=""
                                    />
                                </div>
                                <div className="flex flex-col gap-2">
                                    <Label
                                        className="text-cleanSlate font-instrument text-sm font-medium leading-150"
                                        value="Your brief"
                                    />
                                    {errors?.errors?.canonicalKey && (
                                        <div className="w-full">
                                            <WarningCard
                                                message={errors?.errors?.canonicalKey}
                                            />
                                        </div>
                                    )}
                                    <div className="flex flex-wrap items-center gap-3">
                                        <p className="text-cleanSlate font-instrument text-sm font-bold leading-150">{LINKSTART}</p>
                                        <TextInput
                                            value={displayKey || ''}
                                            onChange={(e) => setDisplayKey(e.target.value)}
                                            onBlur={() => handleBriefUpdate({ displayKey })}
                                            theme={customInputTheme}
                                            sizing="sm"
                                        />
                                        <CopyButton onClick={handleCopy} disabled={isLoading} />
                                    </div>
                                </div>
                            </SectionContainer>
                            <SectionContainer>
                                <div className="flex flex-col gap-5">
                                    <SectionHeader
                                        number="2"
                                        headerText="Add to your email signature"
                                        subText="By sharing your brief in your email signature,
                                        you&apos;ll help more contacts get the most out of
                                        their meetings and communications with you."
                                        customWidth="max-w-[450px]"
                                        showLink
                                        onLinkClick={() => window.open(routes.HELP_PAGES.EMAIL_SIGNATURE, '_blank')
                                            .focus()}
                                    />
                                    <div className="block sm:flex flex-col gap-3">
                                        <EmailSignature
                                            headerText="Professional"
                                            value={composeSignature(' | ')}
                                            isLoading={isLoading}
                                        />
                                        <EmailSignature
                                            headerText="Casual"
                                            value={composeSignature(' <br> ')}
                                            isLoading={isLoading}
                                        />
                                    </div>
                                </div>
                            </SectionContainer>
                            {/* TODO(tom) hide until these features are available
                                requires line fixes to pass linter
                            */}
                            {/* <div className="flex flex-wrap gap-5 md:flex-no-wrap
                                md:gap-0 md:space-x-5 justify-center">
                            <FullFeatures
                                number="3"
                                headerText="Automatically send to the appropriate contacts"
                                subText="Sixty can automatically share your brief with new
                                    contacts or people you haven&apos;t met with in awhile."
                                onClick={handleFullFeatureClick}
                                isLoading={isRedirecting}
                                isModal={isModal}
                            />
                            <FullFeatures
                                number="4"
                                headerText="Automatically add to calendar invites"
                                subText="Sixty can automatically attach your brief to your
                                    meeting invites for all participants to easily access."
                                onClick={handleFullFeatureClick}
                                isLoading={isRedirecting}
                                isModal={isModal}
                            />
                        </div> */}
                        </div>
                    </div>
                </div>
            </PanelContainer>
            {!isModal
                && (
                    <div className="bg-yellow rounded-lg shadow-lg flex flex-col md:flex-row justify-between items-center max-w-[1200px] mx-auto p-5 md:p-10 gap-5">
                        <div className="text-cleanSlate max-w-[536px]">
                            <h2 className="font-bold text-lg">Get all the benefits of Sixty for free!</h2>
                            <p>
                                Sixty can do more than help you have a great meeting.
                                We can help you get the most out of your inbox and
                                calendars, too. Transform your account into a full
                                Sixty account today!
                            </p>
                        </div>
                        <Button
                            color="dark"
                            size="md"
                            className="bg-cleanSlate max-h-[41px] min-w-[210px]"
                            onClick={handleFullFeatureClick}
                            disabled={isLoading}
                            isProcessing={isLoading}
                        >
                            <div className="flex gap-2 items-center">
                                <ChevronRight className="w-5 h-5" />

                                <span className="text-cirrusWhite font-instrument font-medium text-sm leading-7">Get all of Sixty for free</span>
                            </div>
                        </Button>
                    </div>
                )}
        </>
    );
}

export default SharePanel;
